"use client";

import { useState } from "react";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./Tooltip";

type InfoTooltipProps = {
  description: string;
  label?: string;
};

/**
 * Displays a small info icon revealing a multi-sentence description
 * (backend `component_description` copy, typically 300-450 characters) on:
 * - Desktop hover → Radix Tooltip (delayed, dismisses on mouse-leave)
 * - Keyboard focus → Radix Tooltip (instant-open)
 * - Touch tap / click → Radix Popover (toggle; Escape to dismiss)
 *
 * Tooltip is suppressed while the Popover is open so they never overlap.
 * Renders nothing when description is empty.
 */
export function InfoTooltip({ description, label }: InfoTooltipProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (!description) return null;

  const ariaLabel = label ? `About ${label}` : "More information";

  return (
    <TooltipProvider delayDuration={200}>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={ariaLabel}
                className="focus-ring inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full text-text-muted transition-colors hover:text-primary"
              >
                <Info size={16} aria-hidden="true" />
              </button>
            </TooltipTrigger>
          </PopoverTrigger>
          {!popoverOpen && <TooltipContent>{description}</TooltipContent>}
        </Tooltip>
        <PopoverContent>{description}</PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}
